import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import AsyncSelect from "react-select/async";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";

const AccountNew = ({
  accountDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: accountDetails?.frontDocument || "",
    backDocument: accountDetails?.backDocument || "",
  });
  const [initialValues, setInitialValues] = useState({});
  const [editingSections, setEditingSections] = useState({
    account: false,
    documents: false,
  });
  const [loading, setLoading] = useState(false);
  const [accountDetailsError, setAccountDetailsError] = useState(null);
  const {
    updateIqamaInfo,
    fetchIqamaInfoById,
    fileUpload,
    updateEmployeeRegistrationMaster,
    getBankMaster,
    updateAccountDetailsById,
  } = MainServices();

  useEffect(() => {
    getBankOptions();
  }, []);

  const getBankOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getBankMaster(filters);
      console.log("Bank options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setBankOptions(
          response?.data?.data?.doc?.map((bank) => ({
            label: bank.name,
            value: bank.publicId,
          }))
        );
      } else {
        setBankOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadBankOptions = (inputValue, callback) => {
    const filteredOptions = bankOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(inputValue ? filteredOptions : bankOptions);
  };

  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const fileData = {
        file: files[0], // Actual file object
        fileName: files[0].name, // File name
      };

      setFileNames((prev) => ({
        ...prev,
        [name]: fileData,
      }));

      // Update Formik field value for validation
      const fieldName =
        name === "frontDocument" ? "accountFront" : "accountBack";
      setFieldValue(fieldName, files[0]);

      console.log("File uploaded: ", files[0].name);
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveDocument = (documentType, setFieldValue) => {
    setFileNames((prev) => ({
      ...prev,
      [documentType]: "", // Clear the document value
    }));

    // Update Formik field value
    const fieldName =
      documentType === "frontDocument" ? "accountFront" : "accountBack";
    setFieldValue(fieldName, "");

    // Clear the file input value if the document is removed
    if (documentType === "frontDocument") {
      fileInputRef1.current.value = ""; // Clear the file input
    } else if (documentType === "backDocument") {
      fileInputRef2.current.value = ""; // Clear the file input
    }
  };

  console.log("fileNames: , ", fileNames);

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  console.log("iqama details: " + accountDetails);

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    const updatedBankInfo = {
      ...(values?.bank?.value && { bank: values.bank.value }),
      ...(values?.accountHolderName && {
        accountHolderName: values.accountHolderName,
      }),
      ...(values?.ibanNo && { ibanNo: values.ibanNo }),
      ...(values?.ifscCode && { ifscCode: values.ifscCode }),
    };

    setLoading(true);
    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        bankInfo: updatedBankInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("account");
        setAccountDetailsError(null);
        toast("Bank Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update designation", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocuments = async () => {
    if (
      !accountDetails?.bankInfo?.publicId ||
      !accountDetails?.accountHolderName ||
      !accountDetails?.ibanNo
    ) {
      setAccountDetailsError(
        "Please complete all required account details first before submitting documents"
      );
      return;
    } else {
      setAccountDetailsError(null); // Clear error if account details are complete
    }
    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedBankInfo = {
        frontDocument: updatedFileNames.frontDocument,
        backDocument: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          bankInfo: updatedBankInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("Bank Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    }
  };

  const documentValidation = () =>
    Yup.mixed()
      .nullable()
      .notRequired()
      .test(
        "fileType",
        "Only images (JPG, JPEG, PNG) and PDF files are allowed",
        (value) => {
          if (!value) return true; // Allow empty/null values since it's optional

          // Handle both file objects and existing file URLs/paths
          if (typeof value === "string") return true; // Existing document URL
          if (value?.file && typeof value.file === "object") {
            const acceptedTypes = [
              "image/jpeg",
              "image/jpg",
              "image/png",
              "application/pdf",
            ];
            return acceptedTypes.includes(value.file.type);
          }
          if (value?.type) {
            const acceptedTypes = [
              "image/jpeg",
              "image/jpg",
              "image/png",
              "application/pdf",
            ];
            return acceptedTypes.includes(value.type);
          }
          return true;
        }
      )
      .test("fileSize", "File size must be less than 5MB", (value) => {
        if (!value) return true; // Allow empty/null values since it's optional

        // Handle both file objects and existing file URLs/paths
        if (typeof value === "string") return true; // Existing document URL
        if (value?.file && typeof value.file === "object") {
          return value.file.size <= 5 * 1024 * 1024; // 5MB in bytes
        }
        if (value?.size) {
          return value.size <= 5 * 1024 * 1024; // 5MB in bytes
        }
        return true;
      });

  // Updated getValidationSchema function
  const getValidationSchema = (section) => {
    const baseAccountSchema = {
      bank: Yup.object().required("Bank name is required"),
      accountHolderName: Yup.string()
        .matches(
          /^[\u0600-\u06FFA-Za-z\s]+$/,
          "Only alphabets and spaces are allowed"
        )
        .max(50, "Account Holder Name cannot exceed 50 characters")
        .required("Account Holder Name is required"),
      ibanNo: Yup.string()
        .matches(
          /^[\u0600-\u06FFa-zA-Z0-9\u0660-\u0669]+$/,
          "Invalid IBAN format"
        )
        .required("IBAN is required")
        .min(15, "IBAN must be at least 15 characters")
        .max(34, "IBAN must not exceed 34 characters"),
      confirmIban: Yup.string()
        .oneOf([Yup.ref("ibanNo")], "IBANs must match")
        .required("Confirm IBAN is required"),
    };

    const documentSchema = {
      accountFront: documentValidation(),
      accountBack: documentValidation(),
    };

    // Return appropriate schema based on which section is being edited
    if (section === "account") {
      return Yup.object().shape(baseAccountSchema);
    } else if (section === "documents") {
      return Yup.object().shape(documentSchema);
    } else {
      // Return combined schema for full form validation if needed
      return Yup.object().shape({
        ...baseAccountSchema,
        ...documentSchema,
      });
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1.current.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2.current.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        account: false,
        documents: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });

    if (values) {
      setInitialValues(values);
    }
  };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <Formik
          enableReinitialize
          initialValues={{
            bank: accountDetails?.bankInfo
              ? {
                  label: accountDetails.bankInfo.name,
                  value: accountDetails.bankInfo.publicId,
                }
              : accountDetails?.bank || "",
            accountHolderName: accountDetails?.accountHolderName || "",
            ibanNo: accountDetails?.ibanNo || "",
            confirmIban: accountDetails?.ibanNo || "",
            ifscCode: accountDetails?.ifscCode || "",
            accountFront: accountDetails?.frontDocument || "",
            accountBack: accountDetails?.backDocument || "",
          }}
          validationSchema={
            editingSections.account
              ? getValidationSchema("account")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          } // Your validation schema
          onSubmit={(values) => {
            if (editingSections.account) {
              handleSubmit(values);
            } else if (editingSections.documents) {
              handleUpdateDocuments(values);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Bank Account")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !editingSections.account && (
                    <div
                      className="tab-header-edit-container"
                      onClick={() => handleEditToggle("account", values)}
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form className="form-section">
                <div className="add-body-content-grid">
                  {/* Bank */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="bank">
                          {t("Bank")}
                        </label>
                        <span className="add-req-mark">*</span>
                      </div>
                      {editingSections.account ? (
                        <AsyncSelect
                          id="bank"
                          name="bank"
                          value={values.bank}
                          onChange={(selectedOption) =>
                            setFieldValue("bank", selectedOption)
                          }
                          loadOptions={loadBankOptions}
                          defaultOptions={bankOptions}
                          styles={asyncSelectStyles}
                          cacheOptions
                          placeholder={t("Select Bank")}
                        />
                      ) : (
                        <div className="display-value">
                          {values.bank?.label || t("N/A")}
                        </div>
                      )}
                    </div>
                    <CustomErrorMessage
                      name="bank"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* Account Holder Name */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label
                          className="add-label"
                          htmlFor="accountHolderName"
                        >
                          {t("Account Holder Name")}
                        </label>
                        <span className="add-req-mark">*</span>
                      </div>
                      {editingSections.account ? (
                        <input
                          id="accountHolderName"
                          name="accountHolderName"
                          className="add-input"
                          placeholder={t("Enter Account Holder Name")}
                          value={values.accountHolderName}
                          onChange={(e) =>
                            setFieldValue("accountHolderName", e.target.value)
                          }
                        />
                      ) : (
                        <div className="display-value">
                          {values.accountHolderName || t("N/A")}
                        </div>
                      )}
                    </div>
                    <CustomErrorMessage
                      name="accountHolderName"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* IBAN Number */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="ibanNo">
                          {t("IBAN Number")}
                        </label>
                        <span className="add-req-mark">*</span>
                      </div>
                      {editingSections.account ? (
                        <input
                          id="ibanNo"
                          name="ibanNo"
                          className="add-input"
                          placeholder={t("Enter IBAN Number")}
                          value={values.ibanNo}
                          onChange={(e) =>
                            setFieldValue("ibanNo", e.target.value)
                          }
                        />
                      ) : (
                        <div className="display-value">
                          {values.ibanNo || t("N/A")}
                        </div>
                      )}
                    </div>
                    <CustomErrorMessage
                      name="ibanNo"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* Confirm IBAN */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="confirmIban">
                          {t("Confirm IBAN")}
                        </label>
                        <span className="add-req-mark">*</span>
                      </div>
                      {editingSections.account ? (
                        <input
                          id="confirmIban"
                          name="confirmIban"
                          className="add-input"
                          placeholder={t("Confirm IBAN Number")}
                          value={values.confirmIban}
                          onChange={(e) =>
                            setFieldValue("confirmIban", e.target.value)
                          }
                        />
                      ) : (
                        <div className="display-value">
                          {values.confirmIban || t("N/A")}
                        </div>
                      )}
                    </div>
                    <CustomErrorMessage
                      name="confirmIban"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* IFSC Code */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="ifscCode">
                          {t("IFSC Code")}
                        </label>
                      </div>
                      {editingSections.account ? (
                        <input
                          id="ifscCode"
                          name="ifscCode"
                          className="add-input"
                          placeholder={t("Enter IFSC Code")}
                          value={values.ifscCode}
                          onChange={(e) =>
                            setFieldValue("ifscCode", e.target.value)
                          }
                        />
                      ) : (
                        <div className="display-value">
                          {values.ifscCode || t("N/A")}
                        </div>
                      )}
                    </div>
                    <CustomErrorMessage
                      name="ifscCode"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>

                {/* Submit button for Account section (only visible in edit mode) */}
                {editingSections.account && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          handleEditToggle("account");
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.documents && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("documents")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.documents ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {accountDetailsError && (
                      <div className="details-error">{accountDetailsError}</div>
                    )}

                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="accountFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.frontDocument?.fileName ||
                                fileNames?.frontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="frontDocument"
                              ref={fileInputRef1}
                              id="accountFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.frontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "frontDocument",
                                  setFieldValue
                                )
                              } // Pass the document type
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="accountFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="accountBack" className="upload-label">
                            {t("Back Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.backDocument.fileName ||
                                fileNames?.backDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="backDocument"
                              ref={fileInputRef2}
                              id="accountBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.backDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "backDocument",
                                  setFieldValue
                                )
                              } // Pass the document type
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="accountBack"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="accountFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.accountFront
                                ? t("Front Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.accountFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.accountFront,
                                  "Front Document",
                                  "Account Front"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="accountBack" className="upload-label">
                            {t("Back Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.accountBack
                                ? t("Back Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.accountBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.accountBack,
                                  "Back Document",
                                  "Account Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.documents && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("documents")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default AccountNew;
